<script setup lang="ts">
// The machine's clock and its time servers (the machine panel's Network
// tab, under its interfaces): whether it is synchronised, how far off and from which server, and
// the servers to use, a list checked as the node checks it (up to six host
// names or addresses; none: the system's own).
import { computed, ref, watch } from 'vue'
import type { ClockStatus } from '../../api'
import { isHost } from '../../util'
import ListInput from './ListInput.vue'

const props = defineProps<{ clock?: ClockStatus; servers: string[] }>()
const emit = defineEmits<{ save: [servers: string[]] }>()
const list = ref<string[]>([...props.servers])
watch(
    () => props.servers,
    (s, old) => {
        if (JSON.stringify(list.value) === JSON.stringify(old)) list.value = [...s] // not while edited
    },
)
const dirty = computed(() => JSON.stringify(list.value) !== JSON.stringify(props.servers))
const state = computed(() => {
    const c = props.clock
    if (!c) return { dot: 'bg-fg-3', text: 'unknown', detail: '' }
    if (!c.managed)
        return {
            dot: 'bg-fg-3',
            text: 'kept by the host',
            detail: 'a container or WSL: the servers below do not apply here',
        }
    if (!c.synced)
        return { dot: 'bg-bad', text: 'not synchronised', detail: 'the slate’s clock and every timestamp may be off' }
    const off = c.offset_ms !== undefined ? `${c.offset_ms >= 0 ? '+' : ''}${c.offset_ms.toFixed(1)} ms` : ''
    const bad = Math.abs(c.offset_ms ?? 0) > 100
    return {
        dot: bad ? 'bg-warn' : 'bg-ok',
        text: bad ? `synchronised, but ${off} off` : `synchronised${off ? ' · ' + off : ''}`,
        detail: [c.server && `from ${c.server}`, c.daemon].filter(Boolean).join(' · '),
    }
})
</script>

<template>
    <div class="rounded-md border border-line p-2.5 text-xs">
        <div class="flex flex-wrap items-baseline gap-x-2">
            <b class="font-semibold">Clock</b>
            <span class="flex items-center gap-1.5"
                ><span class="inline-block h-2 w-2 rounded-full" :class="state.dot" />{{ state.text }}</span
            >
            <span v-if="state.detail" class="text-fg-3">{{ state.detail }}</span>
        </div>
        <label class="mt-2" for="ntp-servers"
            >Time servers <span class="text-fg-3">· up to six; none: the system’s own</span></label
        >
        <ListInput
            id="ntp-servers"
            v-model="list"
            :max="6"
            :check="(s) => (isHost(s) ? '' : 'a host name or an address')"
            placeholder="ntp.ubuntu.com, or 10.0.0.1"
        />
        <div class="mt-2 flex flex-wrap gap-2">
            <button class="btn-primary text-xs" :disabled="!dirty" @click="emit('save', list)">Save</button>
            <button v-if="dirty" class="btn-ghost text-xs" @click="list = [...servers]">Undo</button>
            <button v-else-if="servers.length" class="btn-ghost text-xs" @click="emit('save', [])">
                Use the system's own
            </button>
        </div>
    </div>
</template>
